import React, { useState } from 'react';
import { Building2, Users, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Clients = () => {
  const [formData, setFormData] = useState({
    name: '', company: '', email: '', phone: '', requirements: '', honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const benefits = [
    { icon: <Shield size={28} />, title: 'Fully Screened Staff', desc: 'Every PCA undergoes police clearance, NDIS screening and reference checks.' },
    { icon: <Clock size={28} />, title: 'Fast Placements', desc: 'Quality candidates shortlisted and placed within days of your request.' },
    { icon: <Users size={28} />, title: 'Right Fit Guarantee', desc: 'We match PCAs to your facility culture, resident needs and shift requirements.' },
    { icon: <Building2 size={28} />, title: 'Ongoing Support', desc: 'Continued support after placement to ensure long-term satisfaction.' },
  ];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/submissions/client`, formData);
      if (res.data.success) {
        toast.success('Thank you. We will contact you within 24 hours.');
        setFormData({ name: '', company: '', email: '', phone: '', requirements: '', honeypot: '' });
      }
    } catch (error) {
      if (error.response?.status === 429) toast.error('Too many submissions. Please try again later.');
      else toast.error('Something went wrong, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="bg-care-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-care-green font-semibold text-sm mb-2 tracking-wide">FOR AGED CARE HOMES</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Request PCAs for Your Facility</h1>
          <p className="text-xl text-white/80 max-w-3xl leading-relaxed">
            Request permanent Personal Care Assistants for your aged care facility. We handle screening, compliance and matching so you can focus on care.
          </p>
        </div>
      </section>

      <section className="py-16 bg-care-light">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-100">
              <div className="text-care-blue mb-3">{b.icon}</div>
              <h3 className="font-semibold text-care-dark mb-2">{b.title}</h3>
              <p className="text-gray-600 text-sm">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-care-dark mb-4">Request PCAs</h2>
            <p className="text-lg text-gray-600">Tell us about your staffing needs and we will be in touch within 24 hours.</p>
          </div>
          <Card className="shadow-xl border-none">
            <CardHeader className="bg-care-blue text-white rounded-t-lg">
              <CardTitle className="text-2xl">Staffing Request Form</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="text" name="honeypot" value={formData.honeypot} onChange={handleChange} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
                <div>
                  <Label htmlFor="name" className="text-base">Contact Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="mt-2 h-12" placeholder="Your full name" data-testid="client-name-input" />
                </div>
                <div>
                  <Label htmlFor="company" className="text-base">Facility Name *</Label>
                  <Input id="company" name="company" value={formData.company} onChange={handleChange} required className="mt-2 h-12" placeholder="Aged care facility name" data-testid="client-company-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-base">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-2 h-12" placeholder="your.email@facility.com.au" data-testid="client-email-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-base">Phone *</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="mt-2 h-12" placeholder="Contact number" data-testid="client-phone-input" />
                </div>
                <div>
                  <Label htmlFor="requirements" className="text-base">Staffing Requirements *</Label>
                  <Textarea id="requirements" name="requirements" value={formData.requirements} onChange={handleChange} required className="mt-2" rows={5} placeholder="Number of PCAs needed, shift times, any specific requirements..." data-testid="client-requirements-input" />
                </div>
                <Button type="submit" size="lg" className="w-full bg-care-blue hover:bg-care-green text-white h-12 text-base font-semibold transition-colors duration-300" disabled={isSubmitting} data-testid="client-submit-button">
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Clients;
